import { useEffect, useState, type ReactNode } from 'react';
import { X } from 'lucide-react';
import { RULED_EXIT_LABEL, type BriefObjectOpenControl, type BriefPickerRow } from '../../lib/brief-card-exits';

/*
 * RdBriefObjectDialog — the RULED brief-object dialog (popout pregeneration build spec §5,
 * rulings 2-9; visual authority: the round-6 mock). A NEW module beside RdCardDialog, not a
 * variant inside it, for the same reason the MVP split composer v2 out of popout-body.ts: the
 * legacy dialog (RdCardDialog) keeps serving the five drill-in surfaces (panels, ledger,
 * stats, tail sheet, conflict room) AND the legacy-brief fallback bit-for-bit until PR C —
 * a shared component would put the frozen path and the ruled path in one render tree.
 *
 * Anatomy (mock, top to bottom):
 *  - header: title + close. No icon chip, no eyebrow — the category vocabulary moves into the
 *    bottom tag chips (ruling 5's register discipline).
 *  - optional lede line (`lede`): ONLY the task card's `detail` and legacy fallback ledes ride
 *    here (spec §5.1: composed body present → the description does NOT render on brief-object
 *    cards; task detail keeps its slot).
 *  - the body zone (scrollable): the stored-body renderer (+ the overlap section on overlap
 *    tasks). Collapses whole via `empty:hidden` when the renderer returns null (P7 invariant —
 *    body nodes must not wrap content in an unconditionally-rendered element).
 *  - bottom rail (outside the scroll area): tag chips + the quiet attribution bottom-left.
 *  - ONE hairline (ruling 4), then the footer: the ONE real open button (ruling 9) beside
 *    Ask on the left, Done right — Ryan's 2026-08-07 placement directive (spec §12): the
 *    open control sits NEXT TO Ask, superseding the earlier bottom-rail-right position.
 *    Hover/focus expands the source picker as a drop-up anchored to the footer — the footer
 *    sits OUTSIDE the scroll container, so the scroll-origin clipping that forced
 *    RdCardDialog's picker into normal flow cannot occur here; the panel caps its own height
 *    and scrolls. Escape stays this dialog's alone — the picker is CSS-positioned content,
 *    never a second overlay (the overlay-stack law's substance).
 *
 * Color (bug-615 token pin): the attribution line CARRIES content → `text-prose-foreground`.
 * Chips, the close button, and picker meta lines are chrome → muted. The attribution class
 * string is pinned by name in the prose-contrast suite (§9).
 */

export interface RdBriefObjectConfig {
  title: string;
  /** Bottom-left tag chips — the card's category vocabulary (catLabel/changeType/stream). */
  chips: string[];
  /** `From {memberName} · {siteLabel} · {relativeTime}` + restricted provenance where set. */
  attribution: string | null;
  /** Task `detail` only (spec §5.1) — brief-object cards pass null (the body's first sentence
   *  IS the lede; ruling 2's one-lede-slot law). */
  lede: string | null;
  body: ReactNode;
  openControl: BriefObjectOpenControl | null;
  askPrefill: string;
  onNavNote: (conversationId: string) => void;
  onAsk: (prefill: string) => void;
}

function OpenControl({
  control,
  onNavNote,
}: {
  control: BriefObjectOpenControl;
  onNavNote: (conversationId: string) => void;
}) {
  const [open, setOpen] = useState(false);

  if (control.kind === 'note') {
    return (
      <button
        type="button"
        data-testid="brief-object-open"
        onClick={() => onNavNote(control.conversationId)}
        className="rounded-lg border border-[#e6dccd] px-[15px] py-[7px] text-sm text-foreground transition hover:bg-muted dark:border-border dark:hover:bg-card"
      >
        {control.label}
      </button>
    );
  }

  const go = (row: BriefPickerRow) => {
    setOpen(false);
    if (row.href) window.open(row.href, '_blank', 'noreferrer');
    else if (row.conversationId) onNavNote(row.conversationId);
  };

  // Touch: a tap fires mouseenter → focus → click in that order, so the hover/focus openers
  // pre-open the menu and the click toggle immediately closes it — the picker never opens.
  // Hover/focus reveal is a hover-pointer affordance; on touch the click is the whole protocol.
  const hoverable =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(hover: hover)').matches;

  return (
    <div
      className="relative"
      data-testid="brief-object-picker-wrap"
      onMouseEnter={hoverable ? () => setOpen(true) : undefined}
      onMouseLeave={hoverable ? () => setOpen(false) : undefined}
      onFocus={hoverable ? () => setOpen(true) : undefined}
      onBlur={e => {
        // Close only when focus leaves the whole wrap (button AND rows), not on row-to-row moves.
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setOpen(false);
      }}
    >
      <button
        type="button"
        data-testid="brief-object-open"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen(v => !v)}
        className="inline-flex items-center gap-[7px] rounded-lg border border-[#e6dccd] px-[15px] py-[7px] text-sm text-foreground transition hover:bg-muted dark:border-border dark:hover:bg-card"
      >
        {control.label}
        <span aria-hidden="true" className={`text-[11px] opacity-55 transition-transform ${open ? 'rotate-180 opacity-100' : ''}`}>
          ▾
        </span>
      </button>
      {open && (
        <div className="absolute bottom-full left-0 z-10 w-[320px] pb-2" data-testid="brief-object-picker">
          <ul
            role="menu"
            className="max-h-[300px] overflow-y-auto rounded-[10px] border border-[#e6dccd] bg-popover p-1.5 shadow-[0_10px_24px_rgba(60,42,18,.14)] dark:border-border dark:shadow-[0_10px_24px_rgba(0,0,0,.4)]"
          >
            {control.rows.map((row, i) => (
              <li key={`${row.href ?? row.conversationId}-${i}`} role="none">
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => go(row)}
                  className="flex w-full flex-col items-start gap-px rounded-[7px] px-2.5 py-2 text-left transition hover:bg-muted dark:hover:bg-card"
                >
                  <span className="text-[13.5px] font-medium leading-5 text-foreground">{row.label}</span>
                  {/* The meta line CARRIES content — it is the ONLY home of the per-member
                    * visibility privacy signal (§5.3) — so it is prose, never muted (bug-615;
                    * same classification as the legacy picker's site line). */}
                  {row.meta && <span className="text-xs leading-4 text-prose-foreground">{row.meta}</span>}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export function RdBriefObjectDialog({ config, onClose }: { config: RdBriefObjectConfig; onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="rd-brief-object-title"
      className="rd fixed inset-0 z-50 flex items-end justify-center bg-[#2d2822]/35 p-3 font-['DM_Sans'] text-foreground backdrop-blur-[2px] sm:items-center sm:p-6"
      onMouseDown={onClose}
    >
      <section
        onMouseDown={e => e.stopPropagation()}
        data-testid="brief-object-dialog"
        className="flex max-h-[92vh] min-h-[420px] w-full max-w-[564px] flex-col overflow-hidden rounded-xl border border-[#efe8d8] bg-popover shadow-[0_12px_28px_rgba(60,42,18,.13)] dark:border-border dark:shadow-[0_10px_26px_rgba(0,0,0,.34)]"
      >
        <div className="flex shrink-0 items-start justify-between gap-3.5 px-[26px] pt-6">
          <h2
            id="rd-brief-object-title"
            className="text-[20px] font-semibold leading-[1.3] tracking-[-.01em] [text-wrap:balance]"
          >
            {config.title}
          </h2>
          <button
            onClick={onClose}
            aria-label="Close dialog"
            className="grid size-7 shrink-0 place-items-center rounded-full bg-muted text-muted-foreground transition hover:text-foreground dark:bg-card"
          >
            <X size={14} />
          </button>
        </div>
        {config.lede && (
          <p className="shrink-0 px-[26px] pt-3.5 text-[15px] leading-6 text-prose-foreground">{config.lede}</p>
        )}
        {/* The ONLY shrinkable zone: long bodies scroll here while the rail + footer stay put.
          * `empty:hidden` = the P7 collapse (body node renders null → the zone vanishes whole,
          * no stray padding band); the filler below owns the mock's `.a-fill` role, so the
          * bottom rail stays pinned whether or not a body rendered. */}
        <div
          data-testid="popout-body-zone"
          className="min-h-0 overflow-y-auto px-[26px] pt-3.5 empty:hidden"
        >
          {config.body}
        </div>
        <div className="min-h-[18px] flex-1 shrink-0" aria-hidden="true" />
        <div className="flex shrink-0 flex-wrap items-center justify-between gap-3.5 px-[26px] pb-[18px] pt-4">
          <div className="flex flex-wrap items-center gap-2">
            {config.chips.map(chip => (
              <span
                key={chip}
                className="rounded-full bg-muted px-[11px] py-[3px] text-[12.5px] text-muted-foreground dark:bg-card"
              >
                {chip}
              </span>
            ))}
            {config.attribution && (
              <span className="ml-1.5 text-[13px] text-prose-foreground" data-testid="brief-object-attribution">
                {config.attribution}
              </span>
            )}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2.5 border-t border-[#e6dccd] px-[26px] py-3.5 dark:border-border">
          {config.openControl && <OpenControl control={config.openControl} onNavNote={config.onNavNote} />}
          <button
            type="button"
            onClick={() => config.onAsk(config.askPrefill)}
            className="rounded-lg border border-[#e6dccd] px-[15px] py-[7px] text-sm text-foreground transition hover:bg-muted dark:border-border dark:hover:bg-card"
          >
            {RULED_EXIT_LABEL.ask}
          </button>
          <div className="flex-1" />
          <button
            onClick={onClose}
            className="rounded-lg bg-foreground px-5 py-2 text-sm font-medium text-card transition hover:opacity-90"
          >
            Done
          </button>
        </div>
      </section>
    </div>
  );
}
